/**
 * LaTeX math in markdown via KaTeX.
 *
 * Syntax (not standard Markdown — common extension):
 *   Inline:  $E = mc^2$   or   \(E = mc^2\)
 *   Block:   $$ \frac{a}{b} $$   or   \[ \frac{a}{b} \]
 *
 * Protect math before other markdown transforms, then restore rendered HTML.
 */
import katex from 'katex';

const PLACEHOLDER = (i: number) => `\uE000MATH${i}\uE001`;

/** Skip bare currency / numbers like $100 or $1.50 */
function looksLikeCurrency(latex: string): boolean {
  return /^[\d.,\s]+$/.test(latex.trim());
}

function renderLatex(latex: string, displayMode: boolean): string {
  try {
    const html = katex.renderToString(latex.trim(), {
      throwOnError: false,
      displayMode,
      output: 'htmlAndMathml',
      strict: 'ignore',
    });
    if (displayMode) {
      return `<div class="my-4 overflow-x-auto text-center katex-block">${html}</div>`;
    }
    return `<span class="katex-inline">${html}</span>`;
  } catch {
    const escaped = latex
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return displayMode
      ? `<div class="my-4 text-center text-red-600 font-mono text-sm">$$${escaped}$$</div>`
      : `<code class="text-red-600">$${escaped}$</code>`;
  }
}

export type MathProtection = {
  text: string;
  restore: (html: string) => string;
};

/**
 * Replace math regions with placeholders so markdown regexes don't touch them.
 * Call restore() on the final HTML to inject KaTeX output.
 */
export function protectMath(text: string): MathProtection {
  if (!text || (text.indexOf('$') === -1 && text.indexOf('\\(') === -1 && text.indexOf('\\[') === -1)) {
    return { text, restore: (html) => html };
  }

  const slots: string[] = [];

  const stash = (latex: string, displayMode: boolean): string => {
    const i = slots.length;
    slots.push(renderLatex(latex, displayMode));
    return PLACEHOLDER(i);
  };

  let protectedText = text;

  // Block: $$ ... $$ (multiline)
  protectedText = protectedText.replace(/\$\$([\s\S]+?)\$\$/g, (_, latex: string) =>
    stash(latex, true),
  );

  // Block: \[ ... \]
  protectedText = protectedText.replace(/\\\[([\s\S]+?)\\\]/g, (_, latex: string) =>
    stash(latex, true),
  );

  // Inline: \( ... \)
  protectedText = protectedText.replace(/\\\((.+?)\\\)/g, (_, latex: string) =>
    stash(latex, false),
  );

  // Inline: $ ... $ (no space right after/before $; skip currency-like)
  protectedText = protectedText.replace(
    /\$([^\s$](?:[^$\n]*?[^\s$])?)\$/g,
    (match, latex: string) => {
      if (looksLikeCurrency(latex)) return match;
      return stash(latex, false);
    },
  );

  return {
    text: protectedText,
    restore: (html: string) => {
      let result = html;
      for (let i = 0; i < slots.length; i++) {
        const token = PLACEHOLDER(i);
        // Placeholders may be split by <br> if newlines were inside — shouldn't happen
        result = result.split(token).join(slots[i]);
      }
      return result;
    },
  };
}

/** Convenience: protect → transform → restore */
export function withMathMarkdown(
  text: string,
  transform: (protectedText: string) => string,
): string {
  const { text: protectedText, restore } = protectMath(text);
  return restore(transform(protectedText));
}

/** Toolbar snippets: body is LaTeX without $ delimiters; selectToken is highlighted after insert */
export type MathSnippet = {
  id: string;
  label: string;
  hint: string;
  /** Unicode preview shown in the menu */
  preview: string;
  displayMode: boolean;
  body: string;
  /** Substring inside body to select after insert (first match) */
  selectToken?: string;
};

export const MATH_SNIPPETS: MathSnippet[] = [
  {
    id: 'inline',
    label: 'Fórmula inline',
    hint: '$...$',
    preview: '𝑥',
    displayMode: false,
    body: 'E = mc^2',
    selectToken: 'E = mc^2',
  },
  {
    id: 'block',
    label: 'Fórmula en bloque',
    hint: '$$...$$',
    preview: '∑',
    displayMode: true,
    body: 'E = mc^2',
    selectToken: 'E = mc^2',
  },
  {
    id: 'frac',
    label: 'Fracción',
    hint: '\\frac{a}{b}',
    preview: 'ᵃ⁄ᵦ',
    displayMode: false,
    body: '\\frac{a}{b}',
    selectToken: '{a}',
  },
  {
    id: 'frac-block',
    label: 'Fracción (bloque)',
    hint: '$$\\frac{a}{b}$$',
    preview: 'ⁿ⁄ₘ',
    displayMode: true,
    body: '\\frac{a}{b}',
    selectToken: '{a}',
  },
  {
    id: 'sum',
    label: 'Sumatorio',
    hint: '\\sum_{i=1}^{n}',
    preview: '∑',
    displayMode: true,
    body: '\\sum_{i=1}^{n} x_i',
    selectToken: 'x_i',
  },
  {
    id: 'prod',
    label: 'Productorio',
    hint: '\\prod_{i=1}^{n}',
    preview: '∏',
    displayMode: true,
    body: '\\prod_{i=1}^{n} x_i',
    selectToken: 'x_i',
  },
  {
    id: 'int',
    label: 'Integral',
    hint: '\\int_{a}^{b}',
    preview: '∫',
    displayMode: true,
    body: '\\int_{a}^{b} f(x)\\,dx',
    selectToken: 'f(x)',
  },
  {
    id: 'sqrt',
    label: 'Raíz cuadrada',
    hint: '\\sqrt{x}',
    preview: '√',
    displayMode: false,
    body: '\\sqrt{x}',
    selectToken: '{x}',
  },
  {
    id: 'nthroot',
    label: 'Raíz n-ésima',
    hint: '\\sqrt[n]{x}',
    preview: 'ⁿ√',
    displayMode: false,
    body: '\\sqrt[n]{x}',
    selectToken: '{x}',
  },
  {
    id: 'pow',
    label: 'Potencia',
    hint: 'x^{n}',
    preview: 'xⁿ',
    displayMode: false,
    body: 'x^{n}',
    selectToken: '{n}',
  },
  {
    id: 'sub',
    label: 'Subíndice',
    hint: 'x_{i}',
    preview: 'xᵢ',
    displayMode: false,
    body: 'x_{i}',
    selectToken: '{i}',
  },
  {
    id: 'lim',
    label: 'Límite',
    hint: '\\lim_{x \\to \\infty}',
    preview: 'lim',
    displayMode: true,
    body: '\\lim_{x \\to \\infty} f(x)',
    selectToken: 'f(x)',
  },
  {
    id: 'binom',
    label: 'Binomial',
    hint: '\\binom{n}{k}',
    preview: 'C(n,k)',
    displayMode: false,
    body: '\\binom{n}{k}',
    selectToken: '{n}',
  },
  {
    id: 'mean',
    label: 'Media',
    hint: '\\bar{x}',
    preview: 'x̄',
    displayMode: false,
    body: '\\bar{x}',
    selectToken: '{x}',
  },
  {
    id: 'pct',
    label: 'Porcentaje',
    hint: '\\%',
    preview: '%',
    displayMode: false,
    body: 'p\\%',
    selectToken: 'p',
  },
  {
    id: 'ci',
    label: 'Intervalo de confianza',
    hint: 'CI = x̄ ± z…',
    preview: 'CI',
    displayMode: true,
    body: 'CI = \\bar{x} \\pm z_{\\alpha/2}\\frac{\\sigma}{\\sqrt{n}}',
    selectToken: '\\bar{x}',
  },
];
